/* eslint-disable @typescript-eslint/ban-ts-comment */
import { QueryDto, QueryDtoSchema } from '@40seas/shared/query';
import startCase from 'lodash/startCase';
import { ZodObject, ZodOptional, ZodSchema, ZodString, ZodTypeAny, ZodTypeDef, ZodUnion, z } from 'zod';
export interface ZodDto<TOutput = any, TDef extends ZodTypeDef = ZodTypeDef, TInput = TOutput> {
    new (): TOutput;
    isZodDto: true;
    schema: ZodSchema<TOutput, TDef, TInput>;
}

// so we can get both 1 and "1"
export function CastToNumber(zodNumber: z.ZodNumber) {
    return z.preprocess((val) => {
        if (!val) return 0;
        return Number(val);
    }, zodNumber);
}

// this for cases when we wanted to have a condition like:
// its ok that the string is not present, but if it is - then it has some condition on it (email, length etc.)
export function OptionalString(zodString: z.ZodString) {
    return z.union([z.null(), zodString, z.string().length(0)]).optional();
}

export const ZodDate = z.date().or(z.string().regex(/^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|([+-]\d{2}:?\d{2}))?)?$/i));

export const ZodObjectId = z.string().regex(/^[0-9a-f]{24}$/i);

export const ZodUrl = z
    .string()
    .regex(/^https?:\/\/((\d{1,3}\.){3}\d{1,3}|([a-zA-Z0-9](?:[a-zA-Z0-9-]{0,256}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,})(:\d+)?([/?#][^\s]*)?$/, {
        message: 'Only valid http and https URLs are allowed',
    });

export function EnumSchema<T>(_enum: T, omitValues?: string[]): ZodSchema {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore - Fix TS FS-3199
    let enumValues = Object.values(_enum);

    if (omitValues) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore - Fix TS FS-3199
        enumValues = enumValues.filter((v) => !omitValues.includes(v));
    }

    return z.enum(enumValues as any);
}

export function FromSchema<TOutput = any, TDef extends ZodTypeDef = ZodTypeDef, TInput = TOutput>(
    schema: ZodSchema<TOutput, TDef, TInput>,
) {
    // @ts-ignore
    class AugmentedZodDto {
        public static isZodDto = true;
        public static schema = schema;
    }
    return AugmentedZodDto;
}

export function QueryDtoOf<T>(Class: new () => T, Classes: Array<new () => any> = []): new () => QueryDto<T> {
    // @ts-ignore
    class AugmentedZodDto extends Class {
        public static isZodDto = true;

        public filter: { eq: { [key: string]: string } } = { eq: {} };
        public static schema = QueryDtoSchema(Class['schema'], Classes);
    }
    return AugmentedZodDto as unknown as new () => QueryDto<T>;
}

export function Omit<T, K extends keyof T>(
    parent: new () => T,
    schemaOmittedProperties: K[],
    schemaOptionalProperties?: K[],
): new () => Omit<T, typeof schemaOmittedProperties[number]> {
    // @ts-ignore
    class AugmentedZodDto extends parent {
        public static isZodDto = true;
        public static schema = getSchemaFromClass(parent, schemaOmittedProperties, schemaOptionalProperties);
    }
    return AugmentedZodDto as unknown as new () => Omit<T, typeof schemaOmittedProperties[number]>;
}

export function Pick<T, P extends keyof T, O extends keyof T>(
    parent: new () => T,
    pickedProperties: P[],
    optionalPickedProperties: O[] = [],
): new () => Pick<T, typeof pickedProperties[number]> & Partial<Pick<T, typeof optionalPickedProperties[number]>> {
    const schemaPickedProperties = Object.fromEntries(pickedProperties.map((key) => [key, true]));
    const schemaOptionalPickedProperties = Object.fromEntries(optionalPickedProperties.map((key) => [key, true]));
    // @ts-ignore
    class AugmentedZodDto extends parent {
        public static isZodDto = true;
        public static schema = parent['schema']
            .pick({ ...schemaPickedProperties, ...schemaOptionalPickedProperties })
            .partial(schemaOptionalPickedProperties)
            .strict();
    }
    return AugmentedZodDto as unknown as new () => Pick<T, typeof pickedProperties[number]> &
        Partial<Pick<T, typeof optionalPickedProperties[number]>>;
}

export function Partial<T>(parent: new () => T): new () => Partial<T> {
    // @ts-ignore
    class AugmentedZodDto extends parent {
        public static isZodDto = true;
        public static schema = parent['schema'].partial();
    }
    return AugmentedZodDto as unknown as new () => Partial<T>;
}

function transformUnionProperties(schemaShape: any): any {
    const transformedShape: any = {};
    for (const key in schemaShape) {
        const schema = schemaShape[key];

        // If the schema is a ZodUnion, check if one of the types is a string
        if (schema instanceof z.ZodUnion) {
            const stringType = schema._def.options.find((option: any) => option instanceof z.ZodString);

            if (stringType) {
                transformedShape[key] = z.string(); // Replace union with string only
            } else {
                transformedShape[key] = schema; // Leave as is if no string type found
            }
        } else if (schema._def.innerType instanceof z.ZodUnion) {
            const stringType = schema._def.innerType._def.options.find((option: any) => option instanceof z.ZodString);

            if (stringType) {
                transformedShape[key] = z.string().optional(); // Replace union with string only
            } else {
                transformedShape[key] = schema; // Leave as is if no string type found
            }
        } else if (schema instanceof z.ZodEffects) {
            // If it's a ZodEffects, wrap it in ZodPipeline to ensure the input/output types are correct
            transformedShape[key] = z.pipeline(z.any(), schema); // Maintain the schema but wrap in pipeline
        } else {
            transformedShape[key] = schema; // Copy non-union types
        }
    }
    return transformedShape;
}

export function With<T, K>(parent: new () => K, newObjectSchema: z.AnyZodObject): new () => T & K {
    class AugmentedZodDto {
        public static isZodDto = true;
        public static schema = parent['schema'].merge(newObjectSchema);
    }
    return AugmentedZodDto as unknown as new () => T & K;
}

export function WithEffect<T>(
    parent: new () => T,
    { check, message }: { check: (...args: any[]) => boolean; message: { message: string; path: string[] } },
): new () => T {
    class AugmentedZodDto {
        public static isZodDto = true;
        public static schema = parent['schema'].refine(check, message);
    }
    return AugmentedZodDto as unknown as new () => T;
}

export function Replace<T>(parent: new () => T, updatedFields: { fieldName: string; newFieldSchema: z.ZodTypeAny }[]): new () => T {
    class AugmentedZodDto {
        public static isZodDto = true;
        public static schema = replaceFieldsInSchema(parent, updatedFields);
    }
    return AugmentedZodDto as unknown as new () => T;
}

function replaceFieldsInSchema(Class: any, updatedFields: { fieldName: string; newFieldSchema: z.ZodTypeAny }[]) {
    const schema = Class['schema'];
    return schema
        .omit(
            updatedFields
                .map((updatedField) => updatedField.fieldName)
                .reduce((prev: any, propertiy: any) => {
                    prev[propertiy] = true;
                    return prev;
                }, {}),
        )
        .merge(
            z.object({
                ...updatedFields.reduce((prev: any, updatedField) => {
                    prev[updatedField.fieldName] = updatedField.newFieldSchema;
                    return prev;
                }, {}),
            }),
        );
}

function transformSchema(schema: ZodObject<any>) {
    const shape = schema.shape;

    const updatedShape = Object.fromEntries(
        Object.entries(shape).map(([key, value]) => {
            if (value instanceof ZodOptional && value._def.innerType._def.options) {
                // Field is a union, check if it contains string and object
                const options = value._def.innerType._def.options;
                const stringOption = options.find((opt) => opt._def.typeName === 'ZodString');
                const hasObject = options.some((opt) => ['ZodObject', 'ZodAny'].includes(opt._def.typeName));

                if (stringOption && hasObject) {
                    return [key, stringOption.optional()];
                }
            }

            if (value instanceof ZodUnion) {
                // Field is a union, check if it contains string and object
                const options = value._def.options;
                const stringOption = options.find((opt) => opt instanceof ZodString);
                const hasObject = options.some((opt) => ['ZodObject', 'ZodAny'].includes(opt._def.typeName));

                if (stringOption && hasObject) {
                    return [key, stringOption];
                }
            }

            return [key, value]; // Keep other fields as is
        }),
    ) as Record<string, ZodTypeAny>;

    return z.object(updatedShape);
}

function getSchemaFromClass(Class: any, schemaOmittedProperties: any[], schemaOptionalProperties?: any[]) {
    const schema = transformSchema(Class['schema']);
    const partialFieldsSchema = schemaOptionalProperties
        ? schema
              .pick(
                  schemaOptionalProperties.reduce((prev: any, property: any) => {
                      prev[property] = true;
                      return prev;
                  }, {}),
              )
              .partial()
        : z.object({});
    if (schema && schemaOmittedProperties?.length) {
        schemaOmittedProperties =
            schemaOptionalProperties && schemaOptionalProperties.length > 0
                ? schemaOmittedProperties.concat(schemaOptionalProperties)
                : schemaOmittedProperties;
        return schema
            .omit(
                schemaOmittedProperties
                    .concat(schemaOptionalProperties ? schemaOptionalProperties : [])
                    .reduce((prev: any, propertiy: any) => {
                        prev[propertiy] = true;
                        return prev;
                    }, {}),
            )
            .merge(partialFieldsSchema)
            .strict();
    }
    return schema;
}

export function requireFields(schema: ZodSchema, fields: string[]): ZodSchema {
    fields.forEach(
        (field) => (schema = schema.refine((data) => data[field], { message: `${startCase(field)} is required`, path: [field] })),
    );
    return schema;
}
